import React from 'react';
import { Modal, Row, Col, Spinner, Badge } from 'reactstrap';
import { motion } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';

const DetailsModal = ({
  isOpen,
  toggle,
  type,
  title,
  mainData,
  relatedData,
  loading,
  formatCreatedDate,
  onTicketClick
}) => {
  const renderMainContent = () => {
    switch (type) {
      case 'asset':
        return (
          <Row className="mb-4">
            <Col md={6}>
              <h4>{mainData?.assetName}</h4>
              <p className="text-muted">{mainData?.assetCategory}</p>
              
              {mainData?.assetImageUrls?.length > 0 && (
                <img
                  src={mainData.assetImageUrls[0]}
                  alt="Asset"
                  className="img-fluid asset-image mb-3"
                  style={{ maxHeight: '200px', objectFit: 'contain', borderRadius: '8px' }}
                />
              )}
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <span className="detail-label" style={{ fontWeight: 500, color: '#495057' }}>Serial Number:</span>
                <span className="detail-value d-block" style={{ color: '#212529' }}>{mainData?.serialNumber || 'N/A'}</span>
              </div>
              <div className="mb-3">
                <span className="detail-label" style={{ fontWeight: 500, color: '#495057' }}>Manufacturer:</span>
                <span className="detail-value d-block" style={{ color: '#212529' }}>{mainData?.manufacturer || 'N/A'}</span>
              </div>
              <div className="mb-3">
                <span className="detail-label" style={{ fontWeight: 500, color: '#495057' }}>Current Location:</span>
                <span className="detail-value d-block" style={{ color: '#212529' }}>{mainData?.currentLocation || 'N/A'}</span>
              </div>
            </Col>
          </Row>
        );
      
      case 'organization':
        return (
          <Row className="mb-4">
            <Col md={4}>
              {mainData?.profileUrl && (
                <img
                  src={mainData.profileUrl}
                  alt="Organization"
                  className="img-fluid rounded mb-3"
                />
              )}
            </Col>
            <Col md={8}>
              <h4>{mainData?.name}</h4>
              <div className="mb-3">
                <span className="detail-label" style={{ fontWeight: 500, color: '#495057' }}>Address:</span>
                <span className="detail-value d-block" style={{ color: '#212529' }}>{mainData?.address || 'N/A'}</span>
              </div>
              <div className="mb-3">
                <span className="detail-label" style={{ fontWeight: 500, color: '#495057' }}>Contact:</span>
                <span className="detail-value d-block" style={{ color: '#212529' }}>{mainData?.contactNumber || 'N/A'}</span>
              </div>
              <div className="mb-3">
                <span className="detail-label" style={{ fontWeight: 500, color: '#495057' }}>Email:</span>
                <span className="detail-value d-block" style={{ color: '#212529' }}>{mainData?.email || 'N/A'}</span>
              </div>
            </Col>
          </Row>
        );
      
      case 'location':
        return (
          <Row className="mb-4">
            <Col md={12}>
              <h4>{mainData}</h4>
              <div className="mb-3">
                <span className="detail-label" style={{ fontWeight: 500, color: '#495057' }}>Organization:</span>
                <span className="detail-value d-block" style={{ color: '#212529' }}>{relatedData[0]?.organization?.name}</span>
              </div>
              <div className="mb-3">
                <span className="detail-label" style={{ fontWeight: 500, color: '#495057' }}>Address:</span>
                <span className="detail-value d-block" style={{ color: '#212529' }}>{relatedData[0]?.organization?.address || 'N/A'}</span>
              </div>
            </Col>
          </Row>
        );
      
      default:
        return null;
    }
  };

  const renderRelatedTitle = () => {
    switch (type) {
      case 'asset': return 'Related Work Orders';
      case 'organization': return 'Recent Work Orders';
      case 'location': return 'Active Work Orders';
      default: return 'Related Items';
    }
  };

  const getStatusBadge = (status) => {
    const badgeClass = {
      'SUBMITTED': 'bg-info',
      'IN_PROGRESS': 'bg-warning',
      'RESOLVED': 'bg-success',
      'ON_HOLD': 'bg-danger',
    }[status] || 'bg-secondary';

    return (
      <Badge className={`status-badge ${badgeClass}`} style={{ fontSize: '0.8rem', padding: '0.35rem 0.65rem' }}>
        {status === 'SUBMITTED' ? 'OPEN': status}
      </Badge>
    );
  };

  const renderRelatedData = () => {
    if (!relatedData || relatedData.length === 0) {
      return (
        <div className="alert alert-info" style={{ maxHeight: '300px', overflowY: 'auto' }}>
          No related items found
        </div>
      );
    }

    const columns = {
      asset: ['Ticket #', 'Issue Date', 'Status', 'Impact'],
      organization: ['Ticket #', 'Asset', 'Issue Date', 'Status'],
      location: ['Ticket #', 'Asset', 'Issue Type', 'Status']
    };

    return (
      <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
        <table className="table table-sm">
          <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
            <tr>
              {columns[type].map(col => <th key={col}>{col}</th>)}
            </tr>
          </thead>
          <tbody>
            {relatedData.map((item) => (
              <tr key={item.id}>
                {type === 'asset' && (
                  <>
                    <td>
                      <span 
                        className="text-primary cursor-pointer hover-underline"
                        onClick={() => onTicketClick(item.ticketNumber)}
                        style={{ cursor: 'pointer' }}
                      >
                        {item.ticketNumber}
                      </span>
                    </td>
                    <td>{formatCreatedDate(item.createdAt)}</td>
                    <td>{getStatusBadge(item.status)}</td>
                    <td>{item.impactOnCare?.replace(/_/g, ' ') || 'N/A'}</td>
                  </>
                )}
                {type === 'organization' && (
                  <>
                    <td>
                      <span 
                        className="text-primary cursor-pointer hover-underline"
                        onClick={() => onTicketClick(item.ticketNumber)}
                        style={{ cursor: 'pointer' }}
                      >
                        {item.ticketNumber}
                      </span>
                    </td>
                    <td>{item.asset?.assetName}</td>
                    <td>{formatCreatedDate(item.createdAt)}</td>
                    <td>{getStatusBadge(item.status)}</td>
                  </>
                )}
                {type === 'location' && (
                  <>
                    <td>
                      <span 
                        className="text-primary cursor-pointer hover-underline"
                        onClick={() => onTicketClick(item.ticketNumber)}
                        style={{ cursor: 'pointer' }}
                      >
                        {item.ticketNumber}
                      </span>
                    </td>
                    <td>{item.asset?.assetName}</td>
                    <td>{ <div>
                        {item?.issueType?.length > 0
                          ? item?.issueType?.map(tag => tag.replace(/_/g, ' ')).join(', ')
                          : 'N/A'}
                      </div> || 'N/A'}</td>
                    <td>{getStatusBadge(item.status)}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      centered
      className="fade-scale-modal"
      style={{ maxHeight: '80vh' }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        <div className="modal-header" style={{ position: 'sticky', top: 0, background: 'white', zIndex: 2 }}>
          <h5 className="modal-title">{title}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={toggle}
            aria-label="Close"
          ></button>
        </div>
        <div className="modal-body" style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          paddingTop: '0',
          maxHeight: 'calc(80vh - 60px)',
          overflow: 'hidden'
        }}>
          <div style={{ flexShrink: 0 }}>
            {renderMainContent()}
            <h5 className="mb-3">{renderRelatedTitle()}</h5>
          </div>
          <div style={{ flexGrow: 1, overflow: 'hidden' }}>
            {loading ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
              </div>
            ) : (
              renderRelatedData()
            )}
          </div>
        </div>
      </motion.div>
    </Modal>
  );
};

export default DetailsModal;